/**
 * Keyframe easings. A keyframe's easing shapes the segment that leaves it and
 * is stored as a string, so new kinds need no schema change:
 * - named curves (EASINGS below),
 * - "cubic-bezier(x1,y1,x2,y2)" like CSS (y may overshoot 0..1),
 * - "smooth": a curve through the neighbouring keyframes (Catmull-Rom), which
 *   turns position keyframes into a curved motion path.
 * The preview and the export both evaluate values through `ease` and
 * `interpolateKeyframes`, so they always agree.
 */

export const EASINGS = [
  'linear',
  'easeIn',
  'easeOut',
  'easeInOut',
  'smooth',
  'backIn',
  'backOut',
  'backInOut',
  'elasticIn',
  'elasticOut',
  'elasticInOut',
  'bounceIn',
  'bounceOut',
  'bounceInOut',
  'hold',
] as const;

export const EASING_LABELS: Record<(typeof EASINGS)[number], string> = {
  linear: 'Linear',
  easeIn: 'Ease in',
  easeOut: 'Ease out',
  easeInOut: 'Ease in-out',
  smooth: 'Smooth (curve through keyframes)',
  backIn: 'Back in (anticipate)',
  backOut: 'Back out (overshoot)',
  backInOut: 'Back in-out',
  elasticIn: 'Elastic in',
  elasticOut: 'Elastic out',
  elasticInOut: 'Elastic in-out',
  bounceIn: 'Bounce in',
  bounceOut: 'Bounce out',
  bounceInOut: 'Bounce in-out',
  hold: 'Hold (jump)',
};

/** Classic CSS curves, as cubic-bezier control points. */
export const BEZIER_PRESETS = {
  ease: [0.25, 0.1, 0.25, 1],
  'ease-in': [0.42, 0, 1, 1],
  'ease-out': [0, 0, 0.58, 1],
  'ease-in-out': [0.42, 0, 0.58, 1],
  'swift-out': [0.55, 0, 0.1, 1],
  anticipate: [0.36, 0, 0.66, -0.56],
  overshoot: [0.34, 1.56, 0.64, 1],
} as const satisfies Record<string, readonly [number, number, number, number]>;

const BEZIER_RE = /^cubic-bezier\(\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\)$/;

/** Control points of a "cubic-bezier(x1,y1,x2,y2)" easing, or null. x values must be within 0..1. */
export function parseBezier(easing: string): [number, number, number, number] | null {
  const m = BEZIER_RE.exec(easing);
  if (!m) return null;
  const p = m.slice(1).map(Number) as [number, number, number, number];
  if (p.some((v) => !Number.isFinite(v)) || p[0] < 0 || p[0] > 1 || p[2] < 0 || p[2] > 1) return null;
  if (Math.abs(p[1]) > 10 || Math.abs(p[3]) > 10) return null;
  return p;
}

export function bezierEasing(x1: number, y1: number, x2: number, y2: number): string {
  const r = (v: number) => Math.round(v * 1000) / 1000;
  return `cubic-bezier(${r(x1)},${r(y1)},${r(x2)},${r(y2)})`;
}

export function isValidEasing(easing: string): boolean {
  return (EASINGS as readonly string[]).includes(easing) || parseBezier(easing) !== null;
}

/** y of a CSS cubic bezier at x (Newton steps, bisection fallback). */
function solveBezier(x1: number, y1: number, x2: number, y2: number, x: number): number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sx = (t: number) => ((ax * t + bx) * t + cx) * t;
  const dsx = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  let t = x;
  for (let i = 0; i < 8; i++) {
    const err = sx(t) - x;
    if (Math.abs(err) < 1e-7) break;
    const d = dsx(t);
    if (Math.abs(d) < 1e-6) break;
    t -= err / d;
  }
  if (t < 0 || t > 1 || Math.abs(sx(t) - x) > 1e-5) {
    let lo = 0;
    let hi = 1;
    t = x;
    for (let i = 0; i < 40; i++) {
      if (sx(t) < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
  }
  return ((ay * t + by) * t + cy) * t;
}

const bezierCache = new Map<string, [number, number, number, number] | null>();

function bounceOut(p: number): number {
  const n = 7.5625;
  const d = 2.75;
  if (p < 1 / d) return n * p * p;
  if (p < 2 / d) return n * (p -= 1.5 / d) * p + 0.75;
  if (p < 2.5 / d) return n * (p -= 2.25 / d) * p + 0.9375;
  return n * (p -= 2.625 / d) * p + 0.984375;
}

const BACK = 1.70158;
const ELASTIC = (2 * Math.PI) / 3;
const ELASTIC_IO = (2 * Math.PI) / 4.5;

/** Eased progress (0..1, may overshoot) for linear progress `p` (0..1). */
export function ease(easing: string, p: number): number {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  switch (easing) {
    case 'linear':
    case 'smooth': // handled with neighbours in interpolateKeyframes
      return p;
    case 'easeIn':
      return p * p * p;
    case 'easeOut':
      return 1 - Math.pow(1 - p, 3);
    case 'easeInOut':
      return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
    case 'hold':
      return 0;
    case 'backIn':
      return (BACK + 1) * p * p * p - BACK * p * p;
    case 'backOut':
      return 1 + (BACK + 1) * Math.pow(p - 1, 3) + BACK * Math.pow(p - 1, 2);
    case 'backInOut': {
      const c = BACK * 1.525;
      return p < 0.5
        ? (Math.pow(2 * p, 2) * ((c + 1) * 2 * p - c)) / 2
        : (Math.pow(2 * p - 2, 2) * ((c + 1) * (p * 2 - 2) + c) + 2) / 2;
    }
    case 'elasticIn':
      return -Math.pow(2, 10 * p - 10) * Math.sin((p * 10 - 10.75) * ELASTIC);
    case 'elasticOut':
      return Math.pow(2, -10 * p) * Math.sin((p * 10 - 0.75) * ELASTIC) + 1;
    case 'elasticInOut':
      return p < 0.5
        ? -(Math.pow(2, 20 * p - 10) * Math.sin((20 * p - 11.125) * ELASTIC_IO)) / 2
        : (Math.pow(2, -20 * p + 10) * Math.sin((20 * p - 11.125) * ELASTIC_IO)) / 2 + 1;
    case 'bounceIn':
      return 1 - bounceOut(1 - p);
    case 'bounceOut':
      return bounceOut(p);
    case 'bounceInOut':
      return p < 0.5 ? (1 - bounceOut(1 - 2 * p)) / 2 : (1 + bounceOut(2 * p - 1)) / 2;
    default: {
      let b = bezierCache.get(easing);
      if (b === undefined) {
        b = parseBezier(easing);
        if (bezierCache.size > 500) bezierCache.clear();
        bezierCache.set(easing, b);
      }
      return b ? solveBezier(b[0], b[1], b[2], b[3], p) : p;
    }
  }
}

// ---------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------

/**
 * Easings a transition's progress can follow (plus "cubic-bezier(…)").
 * Transitions have always run linearly, so that is the default. Progress is
 * clamped to 0..1: ffmpeg's xfade can't go past either end, so curves that
 * overshoot (back, elastic, bezier y outside 0..1) flatten there in both the
 * preview and the export.
 */
export const TRANSITION_EASINGS = ['linear', 'easeIn', 'easeOut', 'easeInOut'] as const;
export const DEFAULT_TRANSITION_EASING = 'linear';

export function isTransitionEasing(easing: string): boolean {
  return (TRANSITION_EASINGS as readonly string[]).includes(easing) || parseBezier(easing) !== null;
}

/** Eased transition progress (0..1) for linear progress `p` through the transition window. */
export function transitionProgress(easing: string | undefined, p: number): number {
  const u = Math.min(1, Math.max(0, p));
  if (!easing || easing === 'linear') return u;
  return Math.min(1, Math.max(0, ease(easing, u)));
}
